import { AnyAction } from '@reduxjs/toolkit';
import { CanceledError } from 'axios';
import { call, delay, put, takeEvery } from 'redux-saga/effects';
import { ApiResponse } from '../../../base/api';
import { Loading } from '../../../base/enums';
import { City, Speciality } from '../../../base/interfaces/config';
import { baseErrorHandler } from '../../../base/redux/sagas/appSaga';
import { appActions } from '../../../base/redux/slices/appSlice';
import { employerApi } from '../../api';
import { ConfigResponseData } from '../../api/interfaces';
import { employerAppActions } from '../slices/employerAppSlice';

function* init() {
    try {
        yield put(appActions.setLoading(Loading.App));

        yield call(getConfig);

        yield put(employerAppActions.setInitialized(true));
    } catch (e) {
        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

export function* getConfig() {
    try {
        const { data }: ApiResponse<ConfigResponseData> = yield call(employerApi.getConfig);
        yield put(employerAppActions.setConfig(data));
    } catch (e) {
        yield call(baseErrorHandler, e);
    }
}

function* specialitySearchDelay({ payload }: AnyAction) {
    yield delay(300);
    try {
        yield call(specialitySearch, payload);
    } catch (e) {
        yield call(baseErrorHandler, e);
    }
}

let specialitySearchRequestController: AbortController | null = null;

function* specialitySearch(search: string) {
    try {
        yield put(appActions.setLoading(Loading.SpecialityAutocomplete));
        if (specialitySearchRequestController) {
            specialitySearchRequestController.abort();
        }
        specialitySearchRequestController = new AbortController();
        const { data }: ApiResponse<Speciality[]> = yield call(
            employerApi.findSpeciality,
            { search },
            specialitySearchRequestController.signal,
        );
        yield put(employerAppActions.specialitySearchSuccess(data));
        yield put(appActions.setLoading(null));
        specialitySearchRequestController = null;
    } catch (e) {
        if (!(e instanceof CanceledError)) {
            yield put(appActions.setLoading(null));
            specialitySearchRequestController = null;
            console.error(e);
        }
    }
}

function* citySearchDelay({ payload }: AnyAction) {
    yield delay(300);
    try {
        yield call(citySearch, payload);
    } catch (e) {
        yield call(baseErrorHandler, e);
    }
}

let citySearchRequestController: AbortController | null = null;

function* citySearch(search: string) {
    try {
        yield put(appActions.setLoading(Loading.CityAutocomplete));
        if (citySearchRequestController) {
            citySearchRequestController.abort();
        }
        citySearchRequestController = new AbortController();
        const { data }: ApiResponse<City[]> = yield call(
            employerApi.findCity,
            { search },
            citySearchRequestController.signal,
        );
        yield put(employerAppActions.citySearchSuccess(data));
        yield put(appActions.setLoading(null));
        citySearchRequestController = null;
    } catch (e) {
        if (!(e instanceof CanceledError)) {
            yield put(appActions.setLoading(null));
            citySearchRequestController = null;
            console.error(e);
        }
    }
}

export function* employerSaga() {
    yield takeEvery(employerAppActions.init, init);
    yield takeEvery(employerAppActions.specialitySearchRequest, specialitySearchDelay);
    yield takeEvery(employerAppActions.citySearchRequest, citySearchDelay);
}
